/**
 * The MOU's document: pick a template, edit the text, preview, generate the PDF.
 *
 * The body is stored already merged — a template is applied once, its
 * {{fields}} filled from this MOU, and from then on it is ordinary text the
 * user edits. Applying a template again replaces the text, so it asks first.
 *
 * Every Generate is a new numbered version. The list below keeps all of them,
 * because "what exactly did we send on the 12th?" has to stay answerable after
 * the text is edited again.
 */

import { useEffect, useState } from "react";
import { Select } from "antd";
import {
  AlertTriangle, Download, Eye, FilePlus2, FileText, History, Loader2, Pencil, Save, X,
} from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import {
  MOU_APPLY_TEMPLATE_URL, MOU_DETAIL_URL, MOU_GENERATE_URL, MOU_TEMPLATES_URL, MOU_VERSION_URL,
} from "../../constants/api";
import HeaderFooterEditor, { isBlankHtml } from "./HeaderFooterEditor";
import MergeFieldEditor from "./MergeFieldEditor";
import EditorWorkspace from "./EditorWorkspace";
import { DOC_CLASSES, withLayoutMarkers } from "./docStyles";
import { PdfPreviewModal, downloadBlob } from "./pdfPreview";

const fmtDateTime = (val) =>
  val ? new Date(val).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";

const KIND_BADGE = {
  draft:         "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  signed:        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  countersigned: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
};

export default function MOUDocumentPanel({ mou, onChanged, bodyStale }) {
  const { fetch, create, update } = useApiMethods();

  const [templates, setTemplates] = useState([]);
  const [fieldLists, setFieldLists] = useState({ layout: [], band: [] });
  const [header, setHeader]       = useState("");
  const [footer, setFooter]       = useState("");
  const [editing, setEditing]     = useState(false);
  const [draft, setDraft]         = useState("");
  const [tplId, setTplId]         = useState(mou.template_id || null);
  const [busy, setBusy]           = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);

  const isSigned = mou.status === "signed";
  const canEdit  = mou.can_edit && ["draft", "sent", "declined", "expired"].includes(mou.status);
  const hasBody  = Boolean((mou.body_html || "").trim());

  useEffect(() => {
    fetch(MOU_TEMPLATES_URL).then((res) => {
      setTemplates(res?.data?.templates || []);
      setFieldLists({ layout: res?.data?.layout_fields || [], band: res?.data?.band_fields || [] });
    }).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const startEdit = () => {
    setDraft(mou.body_html || "");
    setHeader(mou.header_html || "");
    setFooter(mou.footer_html || "");
    setEditing(true);
  };

  const applyTemplate = async (id) => {
    if ((editing ? draft : mou.body_html)?.trim()
        && !window.confirm("Replace the current document text with this template? "
                           + "Your edits to the text will be lost (generated PDFs are kept).")) {
      return;
    }
    setBusy("apply");
    const res = await create(MOU_APPLY_TEMPLATE_URL(mou.id), { template_id: id || null });
    setBusy("");
    if (res?.status !== "success") {
      ToastMsg("error", res?.message || "Could not apply the template");
      return;
    }
    setTplId(res.data.template_id);
    setDraft(res.data.body_html);
    setHeader(res.data.header_html || "");
    setFooter(res.data.footer_html || "");
    setEditing(true);
    ToastMsg("info", "Template applied — review the text, then Save.");
  };

  const save = async () => {
    setBusy("save");
    const res = await update(MOU_DETAIL_URL(mou.id), {
      body_html: draft, header_html: header, footer_html: footer, template_id: tplId,
    });
    setBusy("");
    if (res?.status === "success") {
      ToastMsg("success", "Document saved");
      setEditing(false);
      onChanged();
    } else ToastMsg("error", res?.message || "Failed to save");
  };

  const preview = async () => {
    setBusy("preview");
    try {
      const url = await downloadBlob(MOU_GENERATE_URL(mou.id, true), null, {
        method: "post",
        body: editing ? { body_html: draft, header_html: header, footer_html: footer } : {},
      });
      setPreviewUrl(url);
    } catch (e) {
      ToastMsg("error", e.message || "Could not build the preview");
    } finally {
      setBusy("");
    }
  };

  const generate = async () => {
    if (editing) {
      ToastMsg("info", "Save the document first, then generate the PDF.");
      return;
    }
    setBusy("generate");
    const res = await create(MOU_GENERATE_URL(mou.id), {});
    setBusy("");
    if (res?.status === "success") { ToastMsg("success", res.message || "PDF generated"); onChanged(); }
    else ToastMsg("error", res?.message || "Failed to generate the PDF");
  };

  const downloadVersion = async (v) => {
    setBusy(`v-${v.id}`);
    try {
      const suffix = v.kind === "draft" ? "" : `-${v.kind}`;
      await downloadBlob(MOU_VERSION_URL(mou.id, v.id), `${mou.mou_number}-v${v.version}${suffix}.pdf`);
    } catch (e) {
      ToastMsg("error", e.message || "Could not download");
    } finally {
      setBusy("");
    }
  };

  const versions = mou.versions || [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
          <FileText className="h-4 w-4 text-primary" /> MOU document
          {mou.version > 0 && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              v{mou.version}
            </span>
          )}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <Select
              size="small"
              className="w-56"
              placeholder="Apply a template…"
              value={null}
              disabled={busy === "apply"}
              onChange={(v) => applyTemplate(v === "__builtin" ? null : v)}
              options={[
                ...templates.map((t) => ({ value: t.id, label: t.is_default ? `${t.name} (default)` : t.name })),
                { value: "__builtin", label: "Built-in standard MOU" },
              ]}
            />
          )}
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                <X className="mr-1 h-3.5 w-3.5" /> Discard
              </Button>
              <Button size="sm" disabled={busy === "save"} onClick={save}>
                {busy === "save" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                 : <Save className="mr-1 h-3.5 w-3.5" />}
                Save
              </Button>
            </>
          ) : canEdit && (
            <Button variant="outline" size="sm" onClick={startEdit}>
              <Pencil className="mr-1 h-3.5 w-3.5" /> Edit text
            </Button>
          )}
          {(hasBody || editing) && (
            <Button variant="outline" size="sm" disabled={busy === "preview"} onClick={preview}>
              {busy === "preview" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                  : <Eye className="mr-1 h-3.5 w-3.5" />}
              Preview PDF
            </Button>
          )}
          {canEdit && hasBody && !isSigned && (
            <Button size="sm" disabled={busy === "generate" || editing} onClick={generate}>
              {busy === "generate" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                   : <FilePlus2 className="mr-1 h-3.5 w-3.5" />}
              {mou.version > 0 ? "Generate new version" : "Generate PDF"}
            </Button>
          )}
        </div>
      </div>

      {bodyStale && !editing && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            The contract details changed, but the document text was filled in from the old
            values. Re-apply the template, or edit the text, before generating the PDF.
          </span>
        </div>
      )}

      {editing ? (
        <div className="space-y-3">
          <HeaderFooterEditor header={header} footer={footer} bandFields={fieldLists.band}
                              onChange={(c) => {
                                if ("header" in c) setHeader(c.header);
                                if ("footer" in c) setFooter(c.footer);
                              }} />
          <EditorWorkspace storageKey="document"
                           contentKey={`${header || ""}|${footer || ""}|${draft || ""}`}
                           buildPreview={() => downloadBlob(MOU_GENERATE_URL(mou.id, true), null, {
                             method: "post",
                             body: { body_html: draft, header_html: header, footer_html: footer },
                           })}>
            {({ height, onCursorPage }) => (
              <MergeFieldEditor value={draft} onChange={setDraft} height={height} tables
                                layoutFields={fieldLists.layout} onCursorPage={onCursorPage}
                                pageGuides={{ customHeader: !isBlankHtml(header),
                                              customFooter: !isBlankHtml(footer),
                                              header: isBlankHtml(header) ? "" : header,
                                              footer: isBlankHtml(footer) ? "" : footer }} />
            )}
          </EditorWorkspace>
        </div>
      ) : hasBody ? (
        <div className="max-h-[28rem] overflow-y-auto rounded-md border border-gray-100 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
          {/* Sanitised server-side on every write. */}
          <div className={DOC_CLASSES} dangerouslySetInnerHTML={{ __html: withLayoutMarkers(mou.body_html) }} />
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
          No document text yet.{" "}
          {canEdit ? "Apply a template above to start one, or upload a ready-made MOU below."
                   : "This MOU was handled with uploaded documents."}
        </div>
      )}

      {/* ── versions ─────────────────────────────────────────────── */}
      {versions.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
            <History className="h-3.5 w-3.5" /> Generated versions
          </p>
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
            {versions.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-medium text-gray-900 dark:text-white">v{v.version}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${KIND_BADGE[v.kind] || KIND_BADGE.draft}`}>
                    {v.kind_label}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">{fmtDateTime(v.at)}</span>
                  {v.by && <span className="text-gray-500 dark:text-gray-400">· {v.by}</span>}
                  {v.note && <span className="text-gray-400">· {v.note}</span>}
                </div>
                <Button variant="ghost" size="sm" disabled={busy === `v-${v.id}`}
                        onClick={() => downloadVersion(v)} title="Download">
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <PdfPreviewModal url={previewUrl}
                       title={`Preview — ${mou.mou_number}${editing ? " (unsaved text)" : ""}`}
                       onClose={() => setPreviewUrl(null)} />
    </div>
  );
}
